import { Fragment, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileJson, UploadCloud, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FormAlert } from "@/components/common/Alert";
import { PromptSeedImport } from "@/pages/admin/Pass2Admin";
import { useCatalogue } from "@/context/CatalogueContext";
import { api, errorMessage } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const STATUS = { added: "Added", updated: "Updated", unchanged: "Unchanged", would_overwrite: "Would overwrite" };
const COLL = { teams: "Team", agents: "Agent", workflows: "Workflow" };
const show = (v) => (v === undefined || v === null ? "empty" : typeof v === "string" ? v : JSON.stringify(v));

const Tile = ({ label, value, active, onClick, testid }) => (
  <button type="button" onClick={onClick} aria-pressed={active} data-testid={testid} className={cn("card-elevated p-4 text-left transition-quick hover:border-foreground/30", active && "border-foreground")}>
    <p className="text-sm text-muted-foreground">{label}</p>
    <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
  </button>
);

const readFile = (f) => new Promise((resolve) => {
  const r = new FileReader();
  r.onload = () => { try { resolve({ name: f.name, data: JSON.parse(r.result) }); } catch { resolve({ name: f.name, error: true }); } };
  r.readAsText(f);
});

const Diff = ({ item }) => (
  <TableRow className="bg-muted/40 hover:bg-muted/40"><TableCell colSpan={6} className="p-0">
    <table className="w-full text-left text-sm" data-testid={`import-diff-${item.ref}`}>
      <thead><tr className="text-muted-foreground"><th className="px-4 py-2 font-medium">Field</th><th className="px-4 py-2 font-medium">Old value</th><th className="px-4 py-2 font-medium">New value</th></tr></thead>
      <tbody>{item.diff.map((d) => (
        <tr key={d.field} className="border-t align-top"><td className="px-4 py-2 font-medium">{d.field}{d.edited && <span className="ml-1 text-xs text-warning">(edited in hub)</span>}</td>
          <td className="max-w-[320px] break-words px-4 py-2 text-muted-foreground"><span className="line-clamp-4">{show(d.old)}</span></td><td className="max-w-[320px] break-words px-4 py-2"><span className="line-clamp-4">{show(d.new)}</span></td></tr>
      ))}</tbody>
    </table>
  </TableCell></TableRow>
);

export default function ImportAdmin() {
  const { reload } = useCatalogue();
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [overwrite, setOverwrite] = useState(false);
  const [exclude, setExclude] = useState([]);
  const [owItems, setOwItems] = useState([]);
  const [open, setOpen] = useState([]);
  const [preview, setPreview] = useState(null);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/admin/catalogue/imports").then(({ data: d }) => setHistory(d)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const pick = async (list) => {
    setPreview(null); setError(""); setExclude([]); setOwItems([]);
    const arr = Array.from(list || []);
    if (arr.some((f) => f.size > 5_000_000)) return setError("One of the files is larger than 5 MB. Check it is a catalogue file.");
    const read = await Promise.all(arr.map(readFile));
    const bad = read.filter((r) => r.error).map((r) => r.name);
    if (bad.length) setError(`${bad.join(", ")} ${bad.length === 1 ? "is" : "are"} not valid JSON and will be skipped.`);
    setFiles(read.filter((r) => !r.error));
  };

  const body = (ow = overwrite, ex = exclude, oi = owItems) => ({ files, overwrite: ow, exclude: ex, overwrite_items: oi });
  const runPreview = async (...args) => {
    setBusy(true);
    try { const { data: p } = await api.post("/admin/catalogue/preview", body(...args)); setPreview(p); setError(""); } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  const toggleIn = (list, set, ref, arg) => { const next = list.includes(ref) ? list.filter((x) => x !== ref) : [...list, ref]; set(next); return next; };

  const commit = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/catalogue/import", body());
      toast.success(`Catalogue imported. ${r.summary.added} added, ${r.summary.updated + r.summary.would_overwrite} updated.`);
      setPreview(null); setFiles([]); setExclude([]); setOwItems([]);
      if (inputRef.current) inputRef.current.value = "";
      reload(); loadHistory();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  const items = (preview?.items || []).filter((i) => filter === "all" || i.status === filter);

  return (
    <div className="space-y-8" data-testid="admin-import">
      <section className="card-elevated p-6 md:p-8">
        <h2 className="text-base font-semibold">Import catalogue</h2>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Upload one or more files: the hub file, team files, single agent or workflow files, or a mix. They are merged into one preview. Nothing a file leaves out is deleted, unpublished or hidden.</p>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <input ref={inputRef} id="catalogue-file" type="file" multiple accept="application/json,.json" className="sr-only" aria-label="Catalogue files" onChange={(e) => pick(e.target.files)} data-testid="import-file-input" />
          <Button variant="secondary" onClick={() => inputRef.current?.click()} data-testid="import-choose-file-button"><UploadCloud className="!h-4 !w-4" /> Choose files</Button>
          {files.length > 0 && <span className="text-sm text-muted-foreground" data-testid="import-file-name">{files.length} {files.length === 1 ? "file" : "files"} ready</span>}
        </div>
        {files.length > 0 && (
          <div className="mt-6 space-y-4">
            <ul className="space-y-1.5 text-sm" data-testid="import-file-list">
              {files.map((f) => (
                <li key={f.name} className="flex flex-wrap items-center gap-2"><FileJson className="!h-4 !w-4 text-muted-foreground" aria-hidden="true" /><span className="font-medium">{f.name}</span>
                  <span className="text-muted-foreground">scope {f.data.scope || (f.data.teams ? "hub" : "item")} · version {f.data.version ?? "not set"}</span>
                  {f.data.import_rule && <span className="basis-full pl-6 text-muted-foreground" data-testid="import-rule">Import rule: {f.data.import_rule}</span>}
                </li>
              ))}
            </ul>
            <div className="flex items-start gap-3">
              <Checkbox id="overwrite" checked={overwrite} onCheckedChange={(v) => { setOverwrite(v === true); if (preview) runPreview(v === true); }} data-testid="import-overwrite-checkbox" />
              <div><Label htmlFor="overwrite">Overwrite my edits for the whole import</Label><p className="text-sm text-muted-foreground">Leave unticked to keep every field you changed in the hub. You can also choose per item in the preview.</p></div>
            </div>
            <Button onClick={() => runPreview()} disabled={busy} data-testid="import-preview-button">{busy && !preview ? "Checking" : "Preview changes"}</Button>
          </div>
        )}
        <div className="mt-4"><FormAlert testid="import-error">{error}</FormAlert></div>
      </section>

      {preview && (
        <section className="space-y-4" data-testid="import-preview">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {Object.entries(STATUS).map(([k, label]) => <Tile key={k} label={label} value={preview.summary[k]} active={filter === k} onClick={() => setFilter(filter === k ? "all" : k)} testid={`import-summary-${k}`} />)}
          </div>
          {preview.meta?.import_rule && <p className="rounded-xl bg-muted px-4 py-3 text-sm" data-testid="import-preview-rule">Import rule: {preview.meta.import_rule}</p>}
          {preview.warnings.length > 0 && <FormAlert testid="import-warnings">{preview.warnings.join(" ")}</FormAlert>}
          <div className="card-elevated max-h-[560px] overflow-auto">
            <Table>
              <TableHeader><TableRow><TableHead className="w-10">Include</TableHead><TableHead>Entry</TableHead><TableHead>Type</TableHead><TableHead>Result</TableHead><TableHead>Overwrite my edits</TableHead><TableHead>From</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.map((i) => (
                  <Fragment key={i.ref}>
                    <TableRow data-testid={`import-row-${i.collection}-${i.key}`} className={cn(!i.included && "opacity-55")}>
                      <TableCell><input type="checkbox" checked={i.included} onChange={() => runPreview(overwrite, toggleIn(exclude, setExclude, i.ref))} aria-label={`Include ${i.name}`} data-testid={`import-include-${i.ref}`} /></TableCell>
                      <TableCell>
                        <button type="button" disabled={!i.diff.length} onClick={() => toggleIn(open, setOpen, i.ref)} aria-expanded={open.includes(i.ref)} className="flex items-center gap-1 text-left disabled:cursor-default" data-testid={`import-expand-${i.ref}`}>
                          {i.diff.length > 0 && <ChevronRight className={cn("!h-4 !w-4 transition-transform", open.includes(i.ref) && "rotate-90")} aria-hidden="true" />}
                          <span className="font-medium">{i.name}</span> <span className="text-xs text-muted-foreground">{i.key}</span>
                        </button>
                      </TableCell>
                      <TableCell className="text-sm">{COLL[i.collection]}</TableCell>
                      <TableCell className={cn("text-sm", i.status === "would_overwrite" && "font-medium text-warning")}>{STATUS[i.status]}{i.diff.length > 0 && <span className="text-muted-foreground"> · {i.diff.length} fields</span>}</TableCell>
                      <TableCell>{i.conflict_fields.length > 0 && <input type="checkbox" checked={overwrite || owItems.includes(i.ref)} disabled={overwrite} onChange={() => runPreview(overwrite, exclude, toggleIn(owItems, setOwItems, i.ref))} aria-label={`Overwrite my edits on ${i.name}`} data-testid={`import-overwrite-${i.ref}`} />}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{(i.files || []).join(", ")}</TableCell>
                    </TableRow>
                    {open.includes(i.ref) && <Diff item={i} />}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPreview(null)} data-testid="import-cancel-button">Cancel</Button>
            <Button onClick={commit} disabled={busy} data-testid="import-commit-button">{busy ? "Importing" : "Import catalogue"}</Button>
          </div>
        </section>
      )}

      <PromptSeedImport />

      <section>
        <h2 className="mb-3 text-base font-semibold">Import history</h2>
        {history.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="import-history-empty">No imports yet. Your first import will be listed here.</p> : (
          <ul className="space-y-2" data-testid="import-history">
            {history.map((h) => (
              <li key={h.id} className="rounded-xl border bg-card px-4 py-3 text-sm">
                <span className="font-medium">{formatDateTime(h.at)}</span> by {h.by_name} · {h.summary.added} added, {h.summary.updated} updated, {h.summary.would_overwrite} with edits{h.overwrite ? " (overwritten)" : ""}
                {h.files?.length > 0 && <ul className="mt-1 space-y-0.5 text-muted-foreground">{h.files.map((f) => <li key={f.name}>{f.name} · scope {f.scope} · {f.items?.length ? `changed ${f.items.map((r) => r.split(":")[1]).join(", ")}` : "no changes"}</li>)}</ul>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
